import loadable from '@loadable/component';
import {
  BpkBox,
  BpkBreakpoint,
  BpkGrid,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
  PRODUCT_GROUP,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import cx from 'classnames';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import { useMBDContext } from '../common/MBD/useMBDContext';

import LogoImage from './LogoImage/LogoImage';

import type { ProviderLogosProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ProviderLogos';

import STYLES from './ProviderLogos.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const ProviderLogos = ({
  enableLogoShadow,
  hasSectionBackground,
  headerText,
  openLinkInNewTab,
  providerInfo,
  vertical,
}: ProviderLogosProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!providerInfo?.length) {
    return null;
  }

  return (
    // BpkVessel (not BpkBox): carries tracking data attributes and the
    // full-bleed section background (`calc(-50vw + 50%)` margins/padding),
    // which layout props cannot express.
    <BpkVessel
      data-tracking-element-id={rawComponentName}
      data-testid="provider-logos"
      data-tracking-common-params={buildMBDParam(rawComponentName)}
      className={cx(STYLES.ProviderLogos, {
        [STYLES.ProviderLogosSectionBackground]: hasSectionBackground,
      })}
    >
      {headerText && (
        <BpkBox
          marginBottom={BpkSpacing.LG}
          data-testid="provider-logos-header"
        >
          <BpkSectionHeader title={headerText} />
        </BpkBox>
      )}

      {/* Legacy `bpk-breakpoint-mobile` is max-width 512px, so `base` covers
          <=512px and `small-tablet` (min-width 513px) covers the rest. */}
      <BpkGrid
        templateColumns={{
          base: 'repeat(auto-fit, 6rem)',
          [BpkBreakpoint.SmallTablet]: 'repeat(auto-fit, 7.5rem)',
        }}
        gap={{
          base: BpkSpacing.Base,
          [BpkBreakpoint.SmallTablet]: BpkSpacing.LG,
        }}
        justify={
          vertical === PRODUCT_GROUP.PACKAGES && providerInfo.length >= 5
            ? 'space-between'
            : undefined
        }
      >
        {providerInfo.map((provider, index) => {
          const image = <LogoImage {...provider} />;

          return (
            // BpkVessel: radius and optional shadow (visual, not expressible
            // as layout props) plus tracking data attributes. The fixed
            // logo size with its mobile override is layout, so it lives on
            // the nested BpkBox instead of this element's className.
            <BpkVessel
              data-testid={`provider-logos-header-${index}`}
              data-tracking-element-id={`${rawComponentName}_item_${index}`}
              data-tracking-common-params={buildMBDParam(
                `${rawComponentName}_item`,
                {
                  provider_info: provider.providerName,
                },
              )}
              key={provider.providerName}
              className={cx(STYLES.ProviderLogos__providerLogo, {
                [STYLES.ProviderLogos__providerLogoShadow]: enableLogoShadow,
              })}
            >
              {/* Legacy `bpk-breakpoint-mobile` is max-width 512px, so
                  `base` covers <=512px and `small-tablet` (min-width 513px)
                  covers the rest — same mapping as the BpkGrid above. */}
              <BpkBox
                width={{
                  base: '6rem',
                  [BpkBreakpoint.SmallTablet]: '7.5rem',
                }}
                height={{
                  base: '3rem',
                  [BpkBreakpoint.SmallTablet]: '3.75rem',
                }}
              >
                {provider.link ? (
                  <a
                    className={STYLES.ProviderLogos__providerLogoLink}
                    aria-label={provider.providerName}
                    href={provider.link}
                    target={openLinkInNewTab ? '_blank' : ''}
                    rel="noreferrer"
                  >
                    {image}
                  </a>
                ) : (
                  image
                )}
              </BpkBox>
            </BpkVessel>
          );
        })}
      </BpkGrid>
    </BpkVessel>
  );
};

export default (props: ProviderLogosProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.PROVIDER_LOGOS,
      })
    }
  >
    <ProviderLogos {...props} />
  </IntersectionObserverWrapper>
);
